import { z } from 'zod';
import {
  calibrationOutcomeSchema,
  macroVisualDigestSchema,
  macroVisualPresetSchema,
  macroVisualProfileSchema,
  pixelRegionSchema,
  readyNormalizationSchema,
  visualAnalysisErrorCodeSchema,
  VISUAL_COMPARISON_LIMIT,
} from './common';
import { analysisTargetSchema, blobAssetSchema, sourceImageAssetSchema } from './jobs';
import { sourceGraphIdentitySchema, validateSourceIdentity } from '../macroPackage/workflow';
import { calibrationEvidenceBindingIdentitySchema, compareCalibrationEvidenceBindings, calibrationEvidenceSourceImageSchema } from './evidencePages';

export const backendIdentitySchema = z.object({
  backend: z.literal('d3d11'),
  deviceType: z.enum(['hardware', 'warp']),
  adapterLuid: z.string().min(1).max(64),
  vendorId: z.number().int().nonnegative().max(0xffff),
  deviceId: z.number().int().nonnegative().max(0xffff),
  driverVersion: z.string().min(1).max(128),
  kernelDigest: macroVisualDigestSchema,
  decodePolicy: z.literal('opaque-sdr-no-display-icc-v1'),
}).strict();

export const sourceRowSchema = z.object({
  sourceId: z.string().min(1).max(128),
  encoded: sourceImageAssetSchema,
  pixelDigest: macroVisualDigestSchema,
  width: z.number().int().positive().max(16_384),
  height: z.number().int().positive().max(16_384),
}).strict().superRefine((value, context) => {
  if (value.width * value.height > 16_777_216) {
    context.addIssue({ code: z.ZodIssueCode.custom, message: 'Source exceeds 16 megapixels' });
  }
});
export const referenceRowSchema = z.object({
  referenceId: z.string().min(1).max(128),
  sourceId: z.string().min(1).max(128),
  region: pixelRegionSchema,
  regionKey: z.string().min(1).max(128),
}).strict();
export const bindingRowSchema = z.object({
  nodeId: z.string().min(1).max(128),
  packageId: z.string().uuid().optional(),
  snapshotId: z.string().min(1).max(128),
  referenceId: z.string().min(1).max(128),
  stateId: z.string().min(1).max(128),
}).strict();
export const contextRowSchema = z.object({
  contextId: z.string().min(1).max(128),
  stateIds: z.array(z.string().min(1).max(128)).min(1).max(64),
}).strict();
export const authoredThresholdSchema = z.object({
  nodeId: z.string().min(1).max(128),
  matchThresholdPercent: z.number().int().min(0).max(100),
}).strict();
export const authoredBindingThresholdSchema = calibrationEvidenceBindingIdentitySchema.extend({
  matchThresholdPercent: z.number().int().min(0).max(100),
}).strict();
export type AuthoredBindingThreshold = z.infer<typeof authoredBindingThresholdSchema>;

const boundedRows = <T extends z.ZodTypeAny>(schema: T, maximum: number) => z.array(schema).max(maximum);
export const capturedAnalysisFrameSchema = z.object({ frameId: z.string().min(1).max(128),
  referenceId: z.string().min(1).max(128), stateId: z.string().min(1).max(128), sourceId: z.string().min(1).max(128),
  region: pixelRegionSchema, offsetUs: z.number().int().safe(), role: z.enum(['temporal', 'negative']) }).strict();
export const manifestPageSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('captured'), rows: boundedRows(capturedAnalysisFrameSchema, 128) }).strict(),
  z.object({ kind: z.literal('sources'), rows: boundedRows(sourceRowSchema, 128) }).strict(),
  z.object({ kind: z.literal('references'), rows: boundedRows(referenceRowSchema, 128) }).strict(),
  z.object({ kind: z.literal('bindings'), rows: boundedRows(bindingRowSchema, 128) }).strict(),
  z.object({ kind: z.literal('contexts'), rows: boundedRows(contextRowSchema, 128) }).strict(),
  z.object({ kind: z.literal('nonvisual'), rows: boundedRows(z.object({ nodeId: z.string().min(1).max(128) }).strict(), 128) }).strict(),
  z.object({ kind: z.literal('diagnostics'), rows: boundedRows(z.object({ nodeId: z.string().min(1).max(128),
    reason: z.literal('disconnected') }).strict(), 128) }).strict(),
]);
export const analysisManifestSchema = z.object({
  schemaVersion: z.literal(2),
  profile: macroVisualProfileSchema,
  preset: macroVisualPresetSchema,
  target: analysisTargetSchema,
  manifestDigest: macroVisualDigestSchema,
  sourceIdentity: sourceGraphIdentitySchema.optional(),
  workPlanDigest: macroVisualDigestSchema,
  pages: z.array(blobAssetSchema).max(512),
  scheduleDigest: macroVisualDigestSchema,
  authorContentDigest: macroVisualDigestSchema,
  /** Explicit author thresholds. Omitted nodes inherit their calibrated recommendation. */
  authoredThresholds: z.array(authoredThresholdSchema).max(16_384).optional(),
  /** Resolved region/node overrides, sorted by (nodeId, packageId, regionId, referenceId).
   * Manifest/work-plan certification must include these pins; omissions use calibrated recommendations. */
  authoredBindingThresholds: z.array(authoredBindingThresholdSchema).max(16_384).optional(),
  expectedBindings: z.number().int().nonnegative().max(16_384),
  expectedComparisons: z.number().int().nonnegative().max(VISUAL_COMPARISON_LIMIT),
  expectedChunks: z.number().int().nonnegative().max(8192),
}).strict().superRefine((value, context) => {
  const nodeIds = value.authoredThresholds?.map(item => item.nodeId) ?? [];
  validateSourceIdentity(value, context);
  if (new Set(nodeIds).size !== nodeIds.length) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['authoredThresholds'],
      message: 'Authored threshold node ids must be unique' });
  }
  const bindings = value.authoredBindingThresholds ?? [];
  if (bindings.some((binding, index) => index > 0 && compareCalibrationEvidenceBindings(bindings[index - 1], binding) >= 0)) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['authoredBindingThresholds'],
      message: 'Authored binding thresholds must have sorted unique binding identities' });
  }
});

const referenceIdentity = {
  referenceId: z.string().min(1).max(128),
  sourceDigest: macroVisualDigestSchema,
  regionKey: z.string().min(1).max(128),
  validationDigest: macroVisualDigestSchema,
};
export const referenceNormalizationSchema = z.union([
  z.object({ ...referenceIdentity, ...readyNormalizationSchema.options[0].shape }).strict(),
  z.object({ ...referenceIdentity, ...readyNormalizationSchema.options[1].shape }).strict(),
  z.object({ ...referenceIdentity,
    readiness: z.enum(['invalid', 'inseparable']), errorCode: visualAnalysisErrorCodeSchema,
    spatial: z.null(), hamming: z.null(), hashUsable: z.literal(false),
  }).strict(),
]);

const readyReferenceArtifactSchema = z.object({
  normalization: referenceNormalizationSchema.refine((value) => value.readiness === 'ready'),
  hashHex: z.string().regex(/^[0-9a-f]{64}$/),
  geometry: z.object({
    width: z.number().int().positive().max(16_384),
    height: z.number().int().positive().max(16_384),
    region: pixelRegionSchema,
  }).strict(),
  rgb64: blobAssetSchema,
  rgb128: blobAssetSchema,
  sourceDetail: z.object({ version: z.literal(1), values: blobAssetSchema }).strict().optional(),
  sourceFrequency: z.object({ version: z.literal(1), values: blobAssetSchema }).strict().optional(),
}).strict().superRefine((value, context) => {
  if (value.normalization.readiness !== 'ready') return;
  const frequency = value.normalization.combined?.method === 'channel-envelope-v3';
  const modern = frequency || value.normalization.combined?.method === 'channel-envelope-v2';
  if (modern !== Boolean(value.sourceDetail) || modern !== Boolean(value.normalization.detail)) {
    context.addIssue({ code: z.ZodIssueCode.custom, message: 'Source-detail descriptor and envelope must share a version' });
  }
  if (frequency !== Boolean(value.sourceFrequency) || frequency !== Boolean(value.normalization.frequency)) {
    context.addIssue({ code: z.ZodIssueCode.custom, message: 'Source-frequency descriptor and envelope must share a version' });
  }
  if (value.sourceFrequency && value.sourceFrequency.values.byteCount !== 3072) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['sourceFrequency', 'values', 'byteCount'],
      message: 'Source-frequency descriptor must contain 768 packed words' });
  }
  if (value.sourceDetail && value.sourceDetail.values.byteCount !== 3072) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['sourceDetail', 'values', 'byteCount'],
      message: 'Source-detail descriptor must contain 768 packed words' });
  }
});
const failedReferenceArtifactSchema = z.object({
  normalization: referenceNormalizationSchema.refine((value) => value.readiness !== 'ready'),
  errorCode: visualAnalysisErrorCodeSchema,
}).strict();
export const referenceArtifactRowSchema = z.union([readyReferenceArtifactSchema, failedReferenceArtifactSchema]);

export const evidenceCoverageSchema = z.enum(['synthetic', 'session-separated']);
export const geometryStatusSchema = z.enum(['ok', 'geometry_incompatible']);
export const scheduleCropIdentitySchema = z.object({
  cropSourceId: z.string().min(1).max(128),
  cropRegion: pixelRegionSchema,
  geometryStatus: geometryStatusSchema,
}).strict();
export const calibrationArtifactSchema = z.object({
  schemaVersion: z.literal(2),
  profile: macroVisualProfileSchema,
  preset: macroVisualPresetSchema,
  artifactDigest: macroVisualDigestSchema,
  manifestDigest: macroVisualDigestSchema,
  workPlanDigest: macroVisualDigestSchema,
  /** New pipeline artifacts always identify the exact current native scoring policy. */
  scoringRule: z.literal('raw-max-exact-inliers-10-v2'),
  target: analysisTargetSchema,
  backend: backendIdentitySchema.nullable(),
  sourceIdentity: sourceGraphIdentitySchema.optional(),
  coverageDigest: macroVisualDigestSchema,
  referencePages: z.array(blobAssetSchema).max(512),
  reportPages: z.array(blobAssetSchema).max(512),
  /** Exact scored raster evidence, paged independently of reference and validation reports. */
  evidencePages: z.array(blobAssetSchema).max(512).optional(),
  /** Portable compile paths mapped to immutable campaign storage; the artifact binds every mapping. */
  portableEvidence: z.object({ index: blobAssetSchema,
    assets: z.array(z.union([
      z.object({ asset: blobAssetSchema, storage: blobAssetSchema }).strict(),
      z.object({ asset: calibrationEvidenceSourceImageSchema, storage: sourceImageAssetSchema }).strict().refine(value =>
        value.asset.digest === value.storage.digest && value.asset.byteCount === value.storage.byteCount
        && value.asset.contentType === value.storage.contentType, 'Stored source PNG must preserve its exact pin'),
    ])).max(65_536),
  }).strict().optional(),
  expectedBindings: z.number().int().nonnegative().max(16_384),
  coveredBindings: z.number().int().nonnegative().max(16_384),
  readyBindings: z.number().int().nonnegative().max(16_384),
  evidenceCoverage: evidenceCoverageSchema,
  generalizationCertified: z.boolean(),
  /** One default raw-difference threshold per visual node. New artifacts always include it. */
  recommendedThresholds: z.array(authoredThresholdSchema).max(16_384).optional(),
}).strict().superRefine((value, context) => {
  validateSourceIdentity(value, context);
  if (value.backend === null && (value.expectedBindings !== 0 || value.referencePages.length !== 0 || value.reportPages.length !== 0)) {
    context.addIssue({ code: z.ZodIssueCode.custom, message: 'Only an empty visual workload may omit GPU provenance' });
  }
  if (value.readyBindings > value.coveredBindings || value.coveredBindings > value.expectedBindings) {
    context.addIssue({ code: z.ZodIssueCode.custom, message: 'Artifact coverage counts are inconsistent' });
  }
  if (value.generalizationCertified && value.evidenceCoverage !== 'session-separated') {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ['generalizationCertified'],
      message: 'Synthetic evidence cannot be generalization certified' });
  }
});

export type BackendIdentity = z.infer<typeof backendIdentitySchema>;
export type AnalysisManifest = z.infer<typeof analysisManifestSchema>;
export type ManifestPage = z.infer<typeof manifestPageSchema>;
export type ReferenceNormalization = z.infer<typeof referenceNormalizationSchema>;
export type EvidenceCoverage = z.infer<typeof evidenceCoverageSchema>;
export type GeometryStatus = z.infer<typeof geometryStatusSchema>;
export type ScheduleCropIdentity = z.infer<typeof scheduleCropIdentitySchema>;
export type CalibrationArtifact = z.infer<typeof calibrationArtifactSchema>;
export type ReferenceArtifactRow = z.infer<typeof referenceArtifactRowSchema>;
